// src/pages/dashboard/BranchSelectionScreen.tsx
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCopy, FiExternalLink, FiMoreVertical, FiPlus, FiSettings, FiTrash2 } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Skeleton from '../../components/ui/Skeleton';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectBranches, selectBranchLoading } from '../../store/selectors';
import { cloneBranch, fetchBranches } from '../../store/slices/branchSlice';
import { getBranchDescription, getBranchName } from '../../utils/brandHelper';
import { Branch } from '../../utils/supabaseClient';
import CreateBranchModal from './components/CreateBranchModal';
import DeleteBranchModal from './components/DeleteBranchModal';

interface BranchSelectionScreenProps {
	organizationId: string;
}

const BranchSelectionScreen: React.FC<BranchSelectionScreenProps> = ({ organizationId }) => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const [error, setError] = useState<string | null>(null);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [activeBranchMenu, setActiveBranchMenu] = useState<string | null>(null);
	const [branchToDelete, setBranchToDelete] = useState<Branch | null>(null)

	const dispatch = useAppDispatch();
	const isLoading = useAppSelector(selectBranchLoading);
	const branches = useAppSelector(selectBranches);

	useEffect(() => {
		if (!isLoading && (!branches.length || branches[0].organization_id !== organizationId)) {
			dispatch(fetchBranches(organizationId));
		}
	}, [organizationId, dispatch, isLoading, branches]);

	const handleCloneBranch = async (originalBranch: Branch) => {
		setActiveBranchMenu(null);
		setError(null);

		try {
			await dispatch(cloneBranch(originalBranch.id)).unwrap();
			await dispatch(fetchBranches(organizationId));
		} catch (err) {
			console.error('Error cloning branch:', err);
			setError(t('notifications.unexpectedError'));
		}
	};

	const handleDeleteBranch = async (branch: Branch) => {
		setActiveBranchMenu(null);
		setError(null)

		setBranchToDelete(branch)
		setIsDeleteModalOpen(true)
	}

	const handleCreateBranch = async (newBranch?: Branch) => {
		setIsCreateModalOpen(false);
		if (newBranch) {
			try {
				await dispatch(fetchBranches(organizationId)).unwrap();
			} catch (err) {
				console.error('Error refreshing branches:', err);
				setError(t('notifications.unexpectedError'));
			}
		}
	};

	const handleBranchSelect = (branchId: string) => {
		// Navigate to the branch-specific dashboard
		navigate(`/dashboard/${branchId}`);
	};

	const toggleBranchMenu = (branchId: string) => {
		setActiveBranchMenu(activeBranchMenu === branchId ? null : branchId);
	};

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
			},
		},
	};

	const itemVariants = {
		hidden: { y: 20, opacity: 0 },
		visible: { y: 0, opacity: 1 },
	};

	return (
		<div className="p-4 sm:p-6 max-w-7xl mx-auto">
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				className="mb-8 text-center"
			>
				<h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
					{t('branches.title')}
				</h1>
				<p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
					{t('branches.subTitle')}
				</p>
			</motion.div>

			{error && (
				<motion.div
					initial={{ opacity: 0, y: -10 }}
					animate={{ opacity: 1, y: 0 }}
					className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-6 max-w-2xl mx-auto"
				>
					{error}
				</motion.div>
			)}

			<div className="mb-6 flex justify-center">
				<Button
					onClick={() => setIsCreateModalOpen(true)}
					leftIcon={<FiPlus />}
					size="lg"
				>
					{t('branches.createBranch')}
				</Button>
			</div>

			{isLoading ? (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
					{[1, 2, 3].map((i) => (
						<div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
							<Skeleton height="h-16" width="w-1/2" className="mb-4" />
							<Skeleton height="h-4" width="w-full" className="mb-3" />
							<Skeleton height="h-4" width="w-2/3" className="mb-6" />
							<Skeleton height="h-10" width="w-full" rounded="rounded-md" />
						</div>
					))}
				</div>
			) : branches.length === 0 ? (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					className="text-center p-12 bg-white dark:bg-gray-800 rounded-lg shadow-md"
				>
					<p className="text-gray-600 dark:text-gray-300 mb-6">
						{t('branches.noBranches')}
					</p>
					<Button
						onClick={() => setIsCreateModalOpen(true)}
						leftIcon={<FiPlus />}
					>
						{t('branches.createFirstBranch')}
					</Button>
				</motion.div>
			) : (
				<motion.div
					variants={containerVariants}
					initial="hidden"
					animate="visible"
					className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
				>
					{branches.map((branch) => (
						<motion.div key={branch.id} variants={itemVariants}>
							<Card className="relative h-full group hover:shadow-lg transition-shadow duration-300">
								<div className="absolute top-4 right-4">
									<div className="relative">
										<button
											onClick={() => toggleBranchMenu(branch.id)}
											className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
										>
											<FiMoreVertical className="text-gray-500" />
										</button>

										{activeBranchMenu === branch.id && (
											<div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
												<div className="py-1">
													<button
														onClick={() => handleBranchSelect(branch.id)}
														className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center cursor-pointer"
													>
														<FiSettings className="me-2" />
														{t('branches.manage')}
													</button>
													<Link
														to={`https://dineai.nawaah.org/${branch.branch_key}/menu`}
														target="_blank"
														rel="noopener noreferrer"
														className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
													>
														<FiExternalLink className="me-2" />
														{t('branches.view')}
													</Link>
													<button
														className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center cursor-pointer"
														onClick={() => handleCloneBranch(branch)}
													>
														<FiCopy className="me-2" />
														{t('branches.clone')}
													</button>
													<button
														className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center cursor-pointer"
														onClick={() => handleDeleteBranch(branch)}
													>
														<FiTrash2 className="me-2" />
														{t('branches.delete')}
													</button>
												</div>
											</div>
										)}
									</div>
								</div>

								<div className='h-full flex flex-col'>
									<div className="mb-4 flex justify-center">
										{branch.logo ? (
											<img
												src={branch.logo}
												alt={branch.name || ''}
												className="w-24 h-24 object-contain rounded-lg"
											/>
										) : (
											<div className="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
												<span className="text-3xl font-bold text-gray-400 dark:text-gray-500">
													{getBranchName(branch, i18n.language)?.charAt(0) || '?'}
												</span>
											</div>
										)}
									</div>

									<h3 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-2">
										{getBranchName(branch, i18n.language) || t('branches.untitled')}
									</h3>


									<p className="text-gray-600 dark:text-gray-300 text-center mb-4 line-clamp-2">
										{getBranchDescription(branch, i18n.language) ?? '-'}
									</p>

									<div className="mt-auto pt-4">
										<Button
											onClick={() => handleBranchSelect(branch.id)}
											variant="primary"
											fullWidth
										>
											{t('branches.select')}
										</Button>
									</div>
								</div>
							</Card>
						</motion.div>
					))}
				</motion.div>
			)
			}

			{
				isCreateModalOpen && (
					<CreateBranchModal
						organizationId={organizationId}
						onClose={() => setIsCreateModalOpen(false)}
						onBranchCreated={handleCreateBranch}
					/>
				)
			}

			<DeleteBranchModal
				isOpen={isDeleteModalOpen && !!branchToDelete}
				branch={branchToDelete!}
				onClose={() => {
					setIsDeleteModalOpen(false);
					setBranchToDelete(null);
				}}
				onBranchDeleted={(deletedBranch) => {
					setIsDeleteModalOpen(false);
					setBranchToDelete(null);
				}}
			/>
		</div >
	);
};

export default BranchSelectionScreen;